import React from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Tooltip
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CT } from '../../views/cateringTheme'

// ---- Presentation: the Delivery page's tier editor (DistanceBandEditor) ----
const PAD_X = { xs: 2.5, sm: 4 }
const CARD_TITLE = { fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: CT.text }
const BODY = { fontSize: 15, lineHeight: 1.5, color: CT.body, '& strong': { fontWeight: 700, color: CT.text } }
// 56px range prefix | "Up to (mi)" | "Fee" | 44px delete. The same template on
// the heading row and every tier row keeps the columns lined up.
const TIER_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '44px minmax(0, 1fr) minmax(0, 1fr) 44px', sm: '56px minmax(0, 190px) minmax(0, 190px) 44px' },
  columnGap: { xs: 1, sm: 1.5 },
  alignItems: 'center'
}
const COLUMN_HEAD = { fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: CT.smallLabel }
const SR_ONLY = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0
}
// Each field keeps its label for screen readers; the column heading shows it once.
const TIER_FIELD = {
  width: '100%',
  '& .MuiInputLabel-root': SR_ONLY,
  '& .MuiOutlinedInput-input': { fontSize: { xs: 16, sm: 17 }, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  '& .MuiInputBase-adornedStart': { pl: { xs: '10px', sm: '14px' } },
  '& .MuiInputBase-adornedEnd': { pr: { xs: '10px', sm: '14px' } },
  '& .MuiInputBase-inputAdornedStart': { pr: { xs: '8px', sm: '14px' } },
  '& .MuiInputBase-inputAdornedEnd': { pl: { xs: '10px', sm: '14px' } }
}
const GHOST_BTN = {
  width: 44, height: 44, borderRadius: '10px', color: CT.secondary, bgcolor: 'transparent',
  '&:hover': { bgcolor: CT.ghostHover, color: CT.danger },
  '& .MuiSvgIcon-root': { fontSize: 20 }
}
const DASHED_BTN = {
  height: 44, borderRadius: '10px', px: 2, bgcolor: CT.card, color: CT.text, fontWeight: 700,
  border: `1px dashed ${CT.dashed}`,
  '&:hover': { bgcolor: CT.page, border: `1px dashed ${CT.text}` }
}
const NOTE = {
  flex: '1 1 300px', minWidth: 0, alignItems: 'flex-start',
  bgcolor: CT.noteBg, border: `1px solid ${CT.noteBorder}`, borderRadius: '14px', p: '20px 22px',
  color: CT.label, fontSize: 14, lineHeight: 1.55,
  '& .MuiAlert-message': { p: 0, width: '100%', overflow: 'visible' },
  '& strong': { fontWeight: 700, color: CT.text }
}

/** Saved tiers → the editor's rows (strings, so a half-typed "2." stays put). */
export const bandsToRows = list => (list || []).map(b => ({ maxMiles: String(b.maxMiles), fee: String(b.fee) }))

/**
 * The rows, checked the way the server checks them (miles 0–500, unique; fee
 * $0–$1,000), sorted by distance. Returns { bands } or { error }.
 */
export const parseBandRows = rows => {
  const parsed = rows.map(b => ({ maxMiles: parseFloat(b.maxMiles), fee: parseFloat(b.fee) }))
  for (const [i, b] of parsed.entries()) {
    if (isNaN(b.maxMiles) || b.maxMiles <= 0 || b.maxMiles > 500) return { error: `Tier ${i + 1}: enter a distance between 0 and 500 miles.` }
    if (isNaN(b.fee) || b.fee < 0 || b.fee > 1000) return { error: `Tier ${i + 1}: enter a fee between $0 and $1,000.` }
  }
  if (new Set(parsed.map(b => b.maxMiles)).size !== parsed.length) return { error: 'Each tier needs a distinct "up to" distance.' }
  return { bands: parsed.sort((a, b) => a.maxMiles - b.maxMiles) }
}

/**
 * CateringBandEditor — catering delivery fee tiers, "up to {maxMiles} mi →
 * ${fee}". Controlled: the page holds the rows and saves them with its own
 * button, so the restaurant page and the super admin page can each send them
 * to their own mutation. No rows = the regular tiers are used.
 *
 * Props:
 *   rows      [{ maxMiles: string, fee: string }]
 *   onChange  (rows) => void
 *   title     card heading
 *   intro     line under the heading
 *   empty     what no tiers means, shown while there are none
 *   notes     [node] — the "Good to know" list
 *   error     a validation message to show above the tiers
 */
export default function CateringBandEditor({ rows, onChange, title, intro, empty, notes = [], error }) {
  const addRow = () => {
    const lastMax = rows.length ? parseFloat(rows[rows.length - 1].maxMiles) || 0 : 0
    onChange([...rows, { maxMiles: String(lastMax + 2), fee: '0' }])
  }
  const removeRow = i => onChange(rows.filter((_, idx) => idx !== i))
  const updateRow = (i, key, val) => onChange(rows.map((b, idx) => (idx === i ? { ...b, [key]: val } : b)))

  // Lower bound of a tier (for the "X–" label) = the next-smaller tier's distance.
  const lowerBoundFor = maxMiles => {
    const m = parseFloat(maxMiles)
    let lower = 0
    for (const b of rows) {
      const bm = parseFloat(b.maxMiles)
      if (bm < m && bm > lower) lower = bm
    }
    return lower
  }

  return (
    <Box>
      <Box sx={{ px: PAD_X, pt: { xs: 3, sm: 3.5 } }}>
        <Typography component="h2" sx={CARD_TITLE}>{title}</Typography>
        {intro && <Typography sx={{ ...BODY, mt: 0.75, maxWidth: 760 }}>{intro}</Typography>}
        {error && <Alert severity="error" sx={{ mt: 2.5 }}>{error}</Alert>}
      </Box>

      {/* Tiers left, "Good to know" right; wraps on narrow screens */}
      <Box sx={{ px: PAD_X, py: { xs: 3, sm: 3.5 }, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 5, rowGap: 3.5 }}>
        <Box sx={{ flex: '0 1 520px', minWidth: 0 }}>
          {rows.length === 0 && (
            <Typography sx={{ fontSize: 14, color: CT.secondary, mb: 2, '& strong': { color: CT.text } }}>{empty}</Typography>
          )}

          {rows.length > 0 && (
            <Box aria-hidden sx={{ ...TIER_GRID, mb: 1 }}>
              <span />
              <Typography sx={COLUMN_HEAD}>Up to (mi)</Typography>
              <Typography sx={COLUMN_HEAD}>Fee</Typography>
              <span />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {rows.map((b, i) => (
              <Box key={i} sx={TIER_GRID}>
                <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, lineHeight: 1, color: CT.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {lowerBoundFor(b.maxMiles)}–
                </Typography>
                <TextField
                  label="Up to (mi)"
                  value={b.maxMiles}
                  onChange={e => updateRow(i, 'maxMiles', e.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ min: 0.1, max: 500, step: 0.5 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">mi</InputAdornment> }}
                  sx={TIER_FIELD}
                />
                <TextField
                  label="Fee"
                  value={b.fee}
                  onChange={e => updateRow(i, 'fee', e.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 1000, step: 0.01 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={TIER_FIELD}
                />
                <Tooltip title="Remove tier">
                  <IconButton size="small" aria-label={`Remove tier ${i + 1}`} onClick={() => removeRow(i)} sx={GHOST_BTN}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>

          <Button size="small" startIcon={<AddRoundedIcon />} onClick={addRow} variant="outlined"
            sx={{ ...DASHED_BTN, mt: rows.length ? 1.75 : 0 }}>
            Add tier
          </Button>
        </Box>

        {notes.length > 0 && (
          <Alert severity="info" icon={false} sx={NOTE}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, fontSize: 15 }}>
              <InfoOutlinedIcon aria-hidden sx={{ fontSize: 20, color: CT.noteIcon }} />
              <strong>Good to know</strong>
            </Box>
            <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {notes.map((n, i) => <li key={i}>{n}</li>)}
            </Box>
          </Alert>
        )}
      </Box>
    </Box>
  )
}
